import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Flag } from 'lucide-react';

export type Priority = 'high' | 'medium' | 'low' | undefined;

interface PriorityPickerProps {
  value: Priority;
  onChange: (value: Priority) => void;
  children: React.ReactNode;
  className?: string;
}

const PriorityPicker: React.FC<PriorityPickerProps> = ({ value, onChange, children, className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const popupRef = useRef<HTMLDivElement>(null);

  const priorityOptions: { label: string, value: Priority, color: string }[] = [
    { label: '无优先级', value: undefined, color: 'text-slate-600 dark:text-slate-300' },
    { label: '高优先级', value: 'high', color: 'text-red-500' },
    { label: '中优先级', value: 'medium', color: 'text-blue-500' },
    { label: '低优先级', value: 'low', color: 'text-green-500' },
  ];

  // Update position when opening
  React.useLayoutEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const popupWidth = 128; // w-32 = 8rem = 128px
      
      let popupHeight = 130; // Estimate height based on options
      if (popupRef.current) {
          popupHeight = popupRef.current.offsetHeight;
      }

      // Default: below the trigger
      let top = rect.bottom + 4;
      
      // Default: Align right edge of popup with right edge of trigger
      let left = rect.right - popupWidth;
      
      // Check bottom overflow - flip to top
      if (rect.bottom + popupHeight > window.innerHeight) {
          top = rect.top - popupHeight - 4;
      }
      
      // Check left overflow
      if (left < 10) {
          left = rect.left;
      }
      
      // Check right overflow
      if (left + popupWidth > window.innerWidth) {
          left = window.innerWidth - popupWidth - 10;
      }

      setPosition({ top, left });
    }
  }, [isOpen]);

  // Close on click outside or scroll
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const portalContent = document.getElementById('priority-portal-content');
      if (containerRef.current && !containerRef.current.contains(event.target as Node) && 
          portalContent && !portalContent.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    const handleScroll = (event: Event) => {
        const target = event.target as Node;
        const portalContent = document.getElementById('priority-portal-content');
        if (portalContent && portalContent.contains(target)) {
            return;
        }
        if (isOpen) setIsOpen(false);
    };

    if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('resize', () => setIsOpen(false));
        window.addEventListener('scroll', handleScroll, { capture: true });
    }
    
    return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('resize', () => setIsOpen(false));
        window.removeEventListener('scroll', handleScroll, { capture: true });
    };
  }, [isOpen]);

  return (
    <div className={`relative inline-block ${className || ''}`} ref={containerRef}>
        <div onClick={() => setIsOpen(!isOpen)} className="cursor-pointer">
            {children}
        </div>

        {isOpen && createPortal(
            <AnimatePresence>
                <motion.div
                    ref={popupRef}
                    id="priority-portal-content"
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    style={{ 
                        position: 'fixed',
                        top: position.top, 
                        left: position.left,
                        zIndex: 9999 
                    }}
                    className="bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 w-32 overflow-hidden py-1"
                >
                    {priorityOptions.map(opt => (
                        <button
                            key={String(opt.value)}
                            type="button"
                            onClick={() => {
                                onChange(opt.value);
                                setIsOpen(false);
                            }}
                            className={`w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2 ${
                                value === opt.value ? 'bg-slate-50 dark:bg-slate-700/50' : ''
                            }`}
                        >
                            {opt.value ? (
                                <Flag size={12} className={opt.color} fill="currentColor" />
                            ) : (
                                <div className="w-3" /> // Spacer
                            )}
                            <span className={opt.color}>{opt.label}</span>
                        </button>
                    ))}
                </motion.div>
            </AnimatePresence>,
            document.body
        )}
    </div>
  );
};

export default PriorityPicker;
